import React from "react";
import { useForm, FormContext } from "react-hook-form";
import PT from "prop-types";
import loadable from "@loadable/component";
import { TextField, AbstractControlledField } from "_c/form/Field";
import { generateUrl } from "@/utils/cloudinary";
import Fieldset from "_c/form/Fieldset";
import Label from "_c/form/Label";
import Checkbox from "_c/Checkbox";
import * as S from "./styled";
import { ORDERS_NS } from "@/i18n/ns";
import { Footer, Content } from "_c/Modals";
import PhotoUpload from "./PhotoUpload";
import { useQuery } from "react-query";
import { ReviewsAPI } from "@/api/reviews";
import { QueryKeys } from "@/network/queryKeys";
import QueryRequest from "_c/QueryRequest/withModal";
import { ReviewsService } from "@/services/reviews";
import { useMut } from "@/hooks/useMut";
import { useNotify } from "@/hooks/useNotify";
const NumberInput = loadable(() => import("rc-input-number"));

const formId = "task-review-form";
const ns = `${ORDERS_NS}:review_form`;

const TaskReview = ({ t, task, onClose, review }) => {
  const { notify } = useNotify();
  const mut = useMut(
    async (form) => {
      if (review) {
        await ReviewsService.updateReview(review._id, form);
      } else {
        await ReviewsService.createReview(form);
      }
    },
    {
      notifyOnError: true,
      t,
      onSuccess() {
        if (review) {
          notify(t(`${ORDERS_NS}:review_form.create_success`));
        } else {
          notify(t(`${ORDERS_NS}:review_form.update_success`));
        }
        onClose();
      },
    }
  );
  const methods = useForm({
    defaultValues: {
      rate: review?.rate || 10,
      text: review?.text || "",
      photo: review?.photo || null,
      master: review ? Boolean(review.master) : Boolean(task.master),
      saloon: review ? Boolean(review.saloon) : Boolean(task.saloon),
    },
  });
  const { handleSubmit, watch, register, formState } = methods;
  const { dirty } = formState;
  const watchPhoto = watch("photo");
  const onSubmit = (form) => {
    const norm = {
      ...form,
      master: form.master ? task.master : null,
      saloon: form.saloon ? task.saloon : null,
    };
    if (!review) {
      norm.task = task._id;
    }
    mut.mutate(norm);
  };
  return (
    <>
      <Content>
        <FormContext {...methods}>
          <form id={formId} onSubmit={handleSubmit(onSubmit)}>
            <TextField
              required
              rows="5"
              as="textarea"
              placeholder={t(`${ns}.text_placeholder`)}
              label={t(`${ns}.text`)}
              t={t}
              name="text"
              id="review-text"
            />
            <AbstractControlledField
              t={t}
              Component={NumberInput}
              max={10}
              min={1}
              label={t(`${ns}.rate`)}
              name="rate"
              id="review-rate"
            />
            <Fieldset>
              <Label as="legend">{t(`${ns}.for`)}:</Label>
              <Checkbox
                name="master"
                ref={register}
                label={t("common:master")}
                id="review-for-master"
              />
              <Checkbox
                disabled={!Boolean(task.saloon)}
                name="saloon"
                ref={register}
                label={t("common:salon")}
                id="review-for-saloon"
              />
            </Fieldset>
            <AbstractControlledField
              Component={PhotoUpload}
              name="photo"
              t={t}
              label={t("common:photo")}
              id="review-photo"
              photo={watchPhoto}
            />
            {watchPhoto && <S.Photo src={generateUrl(watchPhoto)} />}
          </form>
        </FormContext>
      </Content>
      <Footer form={formId} type="submit" disabled={mut.isLoading || !dirty}>
        {review ? t("common:change") : t("common:create")}
      </Footer>
    </>
  );
};
TaskReview.propTypes = {
  task: PT.object,
  t: PT.func.isRequired,

  onClose: PT.func.isRequired,
};

const TaskReviewLoaderProxy = (props) => {
  const reviewId = props.task?.review;
  const queryFn = () => ReviewsAPI.findById(reviewId).then(({ data }) => data);
  const query = useQuery(
    [QueryKeys.singleReview, props.task?.review],
    queryFn,
    {
      enabled: Boolean(reviewId),
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: false,
    }
  );
  if (reviewId) {
    return (
      <QueryRequest query={query} onClose={props.onClose}>
        <TaskReview review={query.data} {...props} />
      </QueryRequest>
    );
  }
  return <TaskReview {...props} />;
};

export default TaskReviewLoaderProxy;
